import { PhotographIcon } from "@heroicons/react/outline";
import { useCreate } from "@refinedev/core";
import { Loader } from "components/loader";
import { isEmpty } from "lodash";
import React from "react";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import { bytesForHuman, isInvalidImageSize } from "utills/helpers";

interface DropzoneProps {
  id?: string;
  value?: string[];
  onChange: (urls: string[]) => void;
  [x: string]: any;
}

const ImagesDropzone = React.forwardRef<HTMLDivElement, DropzoneProps>(
  ({ onChange, value = [], ...rest }, ref) => {
    const { mutate, isLoading: isLoading } = useCreate();

    const _uploadFiles = async (file: File) => {
      const formData = new FormData();
      formData.append("upload", file, file.name);

      return new Promise<string>((resolve) => {
        mutate(
          {
            dataProviderName: "externalUpload",
            resource: "/",
            values: formData,
          },
          {
            onSuccess: (data) => {
              const imageUrl = data.data?.result?.img_url;

              resolve(imageUrl || "");
            },
            onError: () => {
              toast.error(`Upload images failed: ${file.name}`);

              resolve("");
            },
          }
        );
      });
    };

    const {
      getRootProps,
      getInputProps,
      open: openUpload,
    } = useDropzone({
      noClick: true,
      noKeyboard: true,
      multiple: true,
      accept: {
        "image/jpeg": [".jpeg", ".png", ".jpg"],
        // "application/pdf": [".pdf"],
      },
      validator: (file: File) => {
        const maximumSize = 3145728; // 3MB

        if (isInvalidImageSize(file, maximumSize)) {
          return {
            code: "invalid-size",
            message: `Invalid file size.\n Maximum size: ${bytesForHuman(
              maximumSize
            )}.`,
          };
        }
        return null;
      },
      onDrop: async (acceptedFiles, rejectedFiles) => {
        if (acceptedFiles.length > 0 && isEmpty(rejectedFiles)) {
          try {
            const urls: string[] = [];

            for (let i = 0; i < acceptedFiles.length; i++) {
              const fileUrl: string = (await _uploadFiles(
                acceptedFiles[i]
              )) as string;

              if (!fileUrl) return;

              urls.push(fileUrl);
            }

            onChange(urls);
          } catch (err) {
            console.log("Upload images failed:", err);

            toast.error("Upload images failed");
          }
        }
      },
      onDropRejected: (errorFiles) => {
        const error = errorFiles[0]?.errors[0];

        if (error?.code === "file-invalid-type") {
          toast.error(
            `Only PNG, JPG, and JPEG file formats are supported`
          );
        } else if (error?.message) {
          toast.error(error.message);
        }
      },
    });

    return (
      <section className="w-full h-full" ref={ref}>
        <div {...getRootProps({ className: "h-full" })}>
          <div
            className="relative overflow-hidden flex flex-col justify-center items-center 
                        rounded-lg border-2 border-dashed border-gray-900/25 bg-gray-50
                        px-6 py-[6px] h-full text-center"
          >
            {isLoading && (
              <div className="bg-gray-900/40 absolute inset-0 flex justify-center items-center rounded-lg">
                <Loader size={40} />
              </div>
            )}

            <div>
              <PhotographIcon
                className="h-[29px] w-[29px] text-[#4F5559] mx-auto"
                aria-hidden="true"
              />

              <input {...getInputProps()} />

              <p className="text-[10px] font-[400] text-[#4F5559]">
                Drag and drop an image
              </p>

              <button
                type="button"
                onClick={openUpload}
                className="text-[10px] font-[700] text-[#011BB6]"
              >
                Browse image
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }
);

ImagesDropzone.displayName = "ImagesDropzone";

export default ImagesDropzone;
